import * as _ from "lodash";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions,
    IBusinessObjectCreateLoadOptions
} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_ControlPoint,
    esWB_ControlPoint_Partition,
    esWB_ControlPoint_Partition_Input,
    esWB_ControlPoint_Type
} from "../PA.Core/PAdbSchema";
import {
    WB_ControlPointPartitionInputRow,
    WB_ControlPointPartitionRow,
    WB_ControlPointRow, WB_ModelCompareRelationshipRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbControlPoint} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {BehaviorSubject} from "rxjs/Rx";
import {ControlPointService} from "../PA.MD.Services/ControlPointService";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export interface IControlPointChangedEvent {
    ControlPointBO: ControlPointBO,
    Row: WB_ControlPointRow,
    PropertyChanged: string,
    PropertyValue: any
}

export class ControlPointBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ControlPoint: WB_ControlPointRow;
    _ControlPointPropertyChanged: BehaviorSubject<IControlPointChangedEvent>

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Control Point'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.IHavePartitions_init();

        this.drWB_ControlPoint = null;
        this._ControlPointPropertyChanged = new BehaviorSubject<IControlPointChangedEvent>(undefined);
    }

    //</editor-fold>

    get WB_ControlPoint(): WB_ControlPointRow {
        return this.drWB_ControlPoint;
    }

    get bEnterpriseScopeEditable():boolean{
        if (_.filter(this.WB_ControlPoint.WB_ControlPointPartitionRows, aRow=>aRow.isActive && aRow.cif.WB_PARTITION_ID != -1).length)
            return false
        else
            return true
    }

    get ControlPointPropertyChanged(): BehaviorSubject<IControlPointChangedEvent> {
        return this._ControlPointPropertyChanged
    }

    private onMainRecordChange(record: WB_ControlPointRow, propertyName: string, propertyValue: any, options: ControlPointBO) {
        this.ControlPointPropertyChanged.next({
            ControlPointBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        let opts = options || {}

        self.LoadProgressProvider.start("Creating Control Point");
        let onLoadComplete = self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for new Control Point.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            this.dbContext.createRecordWithKey(esWB_ControlPoint)
                .then((aNewRow: WB_ControlPointRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_BUSINESSUNIT_ID = 0
                    aNewRow.cif.WB_CONTROL_POINTM_ID = opts.wbMetaTypeId || 0
                    aNewRow.cif.CONTROL_POINT_ID= opts.sId
                    aNewRow.cif.CONTROL_POINT_NAME= opts.sName
                    aNewRow.cif.WB_MDSET_ID= opts.wbMasterDataSetId||0
                    aNewRow.cif.WB_COMPANY_ID= opts.wbCompanyId||0
                    aNewRow.cif.WB_SALESORG_ID= opts.wbSalesOrgId||0
                    // @ts-ignore
                    aNewRow.cif.WB_SALESAREA_ID= opts.wbSalesAreaId||0

                    self.drWB_ControlPoint = aNewRow;
                    self.dbContext.add(esWB_ControlPoint, aNewRow);
                    self.drWB_ControlPoint = aNewRow;
                    self.Identity.setAll(aNewRow.cif.WB_CONTROL_POINT_ID, aNewRow.cif.CONTROL_POINT_ID, aNewRow.cif.CONTROL_POINT_NAME);
                    self.LoadProgressProvider.setName('Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')');

                    self.drWB_ControlPoint.setupOnChangeEvents([], self.onMainRecordChange.bind(self), self)
                    self.Identity.listenToRecordUpdates(self.drWB_ControlPoint,
                        'WB_CONTROL_POINT_ID',
                        'CONTROL_POINT_ID',
                        'CONTROL_POINT_NAME');

                    // Initialize the Control Point Type and BU Keys
                    return Promise.all(
                        [self.dbContext.find(esWB_ControlPoint_Type, opts.wbMetaTypeId)])

                })
                .then((results) => {
                    self.WB_ControlPoint.WB_ControlPointTypeRow = results[0]
                    return self.addPartition(-1)
                }).then((defaultPartitionRow: WB_ControlPointPartitionRow) => {
                return self.IHavePartitions_onLoadComplete()
            })
                .then((ignore) => {
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return onLoadComplete;
    }

    Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            if (self.bIsLoaded) {
                const sError = 'Control Point already loaded.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else if (self.dbContext == null) {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for Control Point.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = Promise.resolve()
            } else {
                recordResolver = ControlPointBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ControlPointBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(() => {
                    const aLoadRow: WB_ControlPointRow = <any>recordResolver.get(esWB_ControlPoint, anId);

                    if (_.isEmpty(aLoadRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception('Id not found.');
                        resolve(self);
                        return;
                    }

                    self.drWB_ControlPoint = aLoadRow;
                    self.drWB_ControlPoint.setupOnChangeEvents([], self.onMainRecordChange.bind(self), self)
                    self.Identity.setAll(aLoadRow.cif.WB_CONTROL_POINT_ID, aLoadRow.cif.CONTROL_POINT_ID, aLoadRow.cif.CONTROL_POINT_NAME);
                    self.LoadProgressProvider.setName('Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')');

                    self.Identity.listenToRecordUpdates(self.drWB_ControlPoint,
                        'WB_CONTROL_POINT_ID',
                        'CONTROL_POINT_ID',
                        'CONTROL_POINT_NAME');

                    return self.IHavePartitions_onLoadComplete()

                })
                .then((ignore) => {
                    if (_.isNumber(self.drWB_ControlPoint.cif.WB_MDSET_ID) && self.drWB_ControlPoint.cif.WB_MDSET_ID > 0) {
                        let mruService = new MDUsageService()
                        return mruService.saveMDSetUsage(self.drWB_ControlPoint.cif.WB_MDSET_ID)
                    }
                    else {
                        self._bIsLoaded = true;
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.finish();
                        resolve(self);
                    }
                })
                .then((ignore) => {

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start('Validating ' + self.WB_ControlPoint.cif.CONTROL_POINT_NAME + '(' + self.WB_ControlPoint.cif.CONTROL_POINT_ID + ')')
        self.ValidateOption = options || {};

        if (self.dbContext == undefined) {
            const sError = 'Unable to determine dbContext to validate Control Point.';
            self.ValidateProgressProvider.exception(sError);
            self._bIsValidated = false;
            return self;
        }

        if (_.isEmpty(self.WB_ControlPoint.cif.CONTROL_POINT_ID)) {
            let sError = "Control Point Id cannot be empty"
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "Id"
            })
        }

        if (_.isEmpty(self.WB_ControlPoint.cif.CONTROL_POINT_NAME)) {
            let sError = "Control Point Name cannot be empty"
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "Name"
            })
        }

        if (_.filter(self.WB_ControlPoint.WB_ControlPointPartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1).length > 1) {
            let sError = "Only can have 1 default partition"
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "DefaultPartition"
            })
        }

        let arrPartition = _.orderBy(_.filter(self.WB_ControlPoint.WB_ControlPointPartitionRows, aRow => aRow.isActive), ['SORT_ORDER'], ['desc'])
        if (arrPartition.length && _.filter(self.WB_ControlPoint.WB_ControlPointPartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1).length > 0 && _.first(arrPartition).cif.WB_PARTITION_ID != -1) {
            let sError = "Default partition will be the last partition"
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "DefaultPartition"
            })
        }

        self._bIsValidated = (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0);
        self.ValidateProgressProvider.finish();
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            const wbControlPoint = <IwbControlPoint>self.drWB_ControlPoint;
            this.SaveProgressProvider.start(
                'Saving ' + wbControlPoint.CONTROL_POINT_NAME + '(' + wbControlPoint.CONTROL_POINT_ID + ')'
            );

            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Control Point.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            await self.Validate(options)
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish()
                return
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await saveFunction(esWB_ControlPoint, self.drWB_ControlPoint, {})
            await saveFunction(esWB_ControlPoint_Partition, self.drWB_ControlPoint.WB_ControlPointPartitionRows, {})
            let partitionInputRows = _.flatMap(self.drWB_ControlPoint.WB_ControlPointPartitionRows, (a) => a.WB_ControlPointPartitionInputRows)
            await saveFunction(esWB_ControlPoint_Partition_Input, partitionInputRows)

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();

        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbControlPoint = <IwbControlPoint>self.drWB_ControlPoint;

        try {

            self.DeleteMessageProvider.clearMessages();
            self.DeleteProgressProvider.start(
                'Deleting ' + wbControlPoint.CONTROL_POINT_NAME + '(' + wbControlPoint.CONTROL_POINT_ID + ')'
            );

            self.drWB_ControlPoint.markDeleted();

            if (bSaveNow)
                await self.SaveUsing(false)

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err)
        }

        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_ControlPoint, opts)
    }

    //</editor-fold>

    //<editor-fold desc = "Partition and Item Operations">

    private _PartitionChanged: BehaviorSubject<{ Record: WB_ControlPointPartitionRow, FieldName: string, FieldValue: any }>
    private _PartitionInputsChanged: BehaviorSubject<{ Record: WB_ControlPointPartitionRow }>

    private IHavePartitions_init(): void {
        // Create local BehaviorSubjects
        this._PartitionChanged = new BehaviorSubject<{ Record: WB_ControlPointPartitionRow; FieldName: string; FieldValue: any }>(undefined)
        this._PartitionInputsChanged = new BehaviorSubject<{ Record: WB_ControlPointPartitionRow }>(undefined)
    }


    private async IHavePartitions_onLoadComplete(): Promise<void> {
        const self = this

        // Register Event handling
        let changeHandler = this.IHavePartitions_onRecordChanged.bind(this)
        _.forEach(this.WB_ControlPoint.WB_ControlPointPartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            aRow.setupOnChangeEvents([], changeHandler, {})
        })

        // Subscribe to Partition Process Changes
        self.PartitionChanged.subscribe((evt) => {
            if (_.isEmpty(evt))
                return

            switch (evt?.FieldName) {
                case "WB_CONTROL_POINTMPR_ID":
                    self.syncPartitionProcessInputs(evt.Record)
                    break;
            }
        })


    }

    IHavePartitions_onRecordChanged(record: ESRecord, propertyName: string, propertyValue: any, options: any): void {
        this.PartitionChanged.next({Record: <any>record, FieldName: propertyName, FieldValue: propertyValue})
    }

    get PartitionChanged(): BehaviorSubject<{ Record: WB_ControlPointPartitionRow; FieldName: string; FieldValue: any }> {
        return this._PartitionChanged
    }

    get ParitionInputsChanged(): BehaviorSubject<{ Record: WB_ControlPointPartitionRow }> {
        return this._PartitionInputsChanged
    }


    async addPartition(wbPartitionId?: number): Promise<WB_ControlPointPartitionRow> {
        const self = this

        let cpPartition
            = <WB_ControlPointPartitionRow>await self.dbContext.createRecordWithKey(esWB_ControlPoint_Partition)
        cpPartition.setDefaults()

        cpPartition.cif.WB_CONTROL_POINT_ID = self.drWB_ControlPoint.cif.WB_CONTROL_POINT_ID
        if (_.isNumber(wbPartitionId))
            cpPartition.cif.WB_PARTITION_ID = wbPartitionId
        cpPartition.cif.SORT_ORDER = self.WB_ControlPoint.WB_ControlPointPartitionRows.length
        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        if (wbPartitionId !== -1) {
            cpPartition.setupOnChangeEvents([], self.IHavePartitions_onRecordChanged.bind(self), {})
        }
        self.dbContext.add(esWB_ControlPoint_Partition, cpPartition)
        let DefaultRow = _.first(_.filter(self.WB_ControlPoint.WB_ControlPointPartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1))
        if (!_.isEmpty(DefaultRow))
            DefaultRow.cif.SORT_ORDER = self.WB_ControlPoint.WB_ControlPointPartitionRows.length
        return cpPartition
    }

    async removePartition(wbControlPointPRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_ControlPoint.WB_ControlPointPartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            if (aRow.cif.WB_CONTROL_POINTPR_ID == wbControlPointPRId) {
                aRow.markDeleted()
                _.forEach(aRow.WB_ControlPointPartitionInputRows, (aInputRow) => {
                    if (!aInputRow.isActive)
                        return

                    aInputRow.markDeleted()
                })
            }
        })

    }

    // This may need some synchronization, it's not re-entrant.
    async syncPartitionProcessInputs(aPartitionRow: WB_ControlPointPartitionRow): Promise<void> {
        const self = this

        let arrInputs = await ControlPointService.getProcessInputs(self.dbContext, aPartitionRow?.cif?.WB_CONTROL_POINTMPR_ID || -1)

        let dctInputs = _.keyBy(arrInputs, (a) => a.cif.WB_CONTROL_POINTMPIR_ID)
        let dctInputsMissing = _.clone(dctInputs)
        let dctExisting: any = {}

        _.forEach(aPartitionRow.WB_ControlPointPartitionInputRows, (aRow) => {
            if (!aRow.isActive)
                return

            let wbControlPointProcessInputId = aRow.cif.WB_CONTROL_POINTMPIR_ID
            if (wbControlPointProcessInputId in dctExisting) {
                aRow.markDeleted() // remove duplicate
                return
            }

            dctExisting[wbControlPointProcessInputId] = wbControlPointProcessInputId

            if (wbControlPointProcessInputId in dctInputs) {
                // Exists and Valid
                delete dctInputsMissing[wbControlPointProcessInputId]
            } else {
                // Exists and Extra/ InValid
                aRow.markDeleted()
            }
            //--
        })

        // Add missing rows
        for (let wbId in dctInputsMissing) {
            let cptProcessInput = dctInputs[wbId]
            let newPI = <WB_ControlPointPartitionInputRow>await self.dbContext.createRecordWithKey(esWB_ControlPoint_Partition_Input)
            newPI.cif.WB_CONTROL_POINTPR_ID = aPartitionRow.cif.WB_CONTROL_POINTPR_ID
            newPI.cif.WB_CONTROL_POINTMPIR_ID = cptProcessInput.cif.WB_CONTROL_POINTMPIR_ID
            newPI.WB_ControlPointTypeProcessInputRow = cptProcessInput
            self.dbContext.add(esWB_ControlPoint_Partition_Input, newPI)
        }

        self.ParitionInputsChanged.next({Record: aPartitionRow})

    }


    //</editor-fold>


    //<editor-fold desc = "IRecord Resolver operations <static>">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const controlPoint_options: CreateOptions = {
            standardConditions: {
                [esWB_ControlPoint]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_ControlPoint_Partition]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_ControlPoint_Partition_Input]: {"DELETED_IND": {"notIn": ['1']}}
            }
            ,
            dctIncludeBelongsToNamedRelations: {}
        }

        return new ESRecordResolver(dbContext, controlPoint_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_ControlPoint, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }


    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(ControlPointBO, "ControlPointBO", "Control Point")
